import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CreateMessageGenSessionRequest, MessageGenSession, MessageGenState } from '@trinity/chat';
import { MessageGenActionType } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useLoadChatInstance } from '@trinity/chat-components/components/Chat.History/hooks/index.js';
import {
  type MessageGenerationCreateSession,
  type MessageGenerationTableActions,
  MessageGenerationCreateSessionContext,
  MessageGenerationDispatchContext,
  MessageGenerationLoadSessionContext,
  MessageGenerationResetSessionContext,
  MessageGenerationStateContext,
  MessageGenerationTableActionsContext,
  MessageGenerationTableActionsValueContext,
} from './Context';

interface Props {
  children: ReactNode;
}

export const MessageGenerationStateContainer = ({ children }: Props): ReactNode => {
  const apiClient = useChatApiClient();
  const loadChatInstance = useLoadChatInstance();

  const [session, setSession] = useState<MessageGenSession | null>(null);
  const [tableActions, setTableActions] = useState<MessageGenerationTableActions | null>(null);

  const { mutate: createMutation } = useMutation({
    mutationFn: (data: CreateMessageGenSessionRequest) => apiClient.messageGeneration.createSession(data),
    onSuccess: (data) => {
      console.log('Created message generation session:', data);
      setSession(data);
    },
  });

  const loadSession = useCallback(
    (messageGenSessionId: number) => {
      apiClient.messageGeneration.getSession(messageGenSessionId).then((data) => {
        setSession(data);
        // Load the chat instance if it exists
        if (data.chatInstanceIdentifier) {
          loadChatInstance(data.chatInstanceIdentifier);
        }
      });
    },
    [apiClient, loadChatInstance]
  );

  const createSession: MessageGenerationCreateSession = useCallback(
    ({ brandIdentifier, sessionId, initialState }) => {
      console.log('Creating message generation session', sessionId, brandIdentifier, initialState);
      createMutation({ sessionId, brandIdentifier, initialState });
    },
    [createMutation]
  );

  const dispatch = useCallback((state: MessageGenState) => {
    console.log('Updating message generation session state:', state);
    // @ts-ignore
    setSession((prev) => (prev ? { ...prev, state } : prev));
  }, []);

  const resetSession = useCallback(() => {
    if (!session?.id) return;
    apiClient.messageGeneration.dispatchActions(session.id, [{ type: MessageGenActionType.ResetPipeline }]);
  }, [session?.id, apiClient]);

  return (
    <MessageGenerationStateContext.Provider value={session}>
      <MessageGenerationDispatchContext.Provider value={dispatch}>
        <MessageGenerationCreateSessionContext.Provider value={createSession}>
          <MessageGenerationLoadSessionContext.Provider value={loadSession}>
            <MessageGenerationResetSessionContext.Provider value={resetSession}>
              <MessageGenerationTableActionsContext.Provider value={setTableActions}>
                <MessageGenerationTableActionsValueContext.Provider value={tableActions}>
                  {children}
                </MessageGenerationTableActionsValueContext.Provider>
              </MessageGenerationTableActionsContext.Provider>
            </MessageGenerationResetSessionContext.Provider>
          </MessageGenerationLoadSessionContext.Provider>
        </MessageGenerationCreateSessionContext.Provider>
      </MessageGenerationDispatchContext.Provider>
    </MessageGenerationStateContext.Provider>
  );
};
